// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { UserIcon } from "src/assets/icons";

import clinicLanguageImg from "src/assets/images/operating-manual/instructions-for-use/clinic-language.png";
import clinicLanguageFrImg from "src/assets/images/operating-manual/instructions-for-use/clinic-language-fr.png";
import clinicOverviewImg from "src/assets/images/operating-manual/instructions-for-use/clinic-overview.png";
import clinicOverviewFrImg from "src/assets/images/operating-manual/instructions-for-use/clinic-overview-fr.png";
import clinicPackageImg from "src/assets/images/operating-manual/instructions-for-use/clinic-package.png";
import clinicPackageFrImg from "src/assets/images/operating-manual/instructions-for-use/clinic-package-fr.png";
import eyeDisplayOrderImg from "src/assets/images/operating-manual/instructions-for-use/eye-display-order.png";
import eyeDisplayOrderFrImg from "src/assets/images/operating-manual/instructions-for-use/eye-display-order-fr.png";
import reportLayoutSettingsImg from "src/assets/images/operating-manual/instructions-for-use/report-layout-settings.png";
import reportLayoutSettingsFrImg from "src/assets/images/operating-manual/instructions-for-use/report-layout-settings-fr.png";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Styles
import { ModalCustomConfirm } from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

const ConfigureClinicSettings = () => {
  const { t } = useTranslation("operating-manual");

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            1. {t("Click the ")}
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <UserIcon />
            </IconButton>
            {t(" profile button on the navigation bar and select Settings.")}
          </Typography>
          <Typography mt="20px">
            2. {t("Select the preferred clinic language and patient language.")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={
                currentLang === "fr" ? clinicLanguageFrImg : clinicLanguageImg
              }
              width="100%"
              alt="clinic-language"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            3.{" "}
            {t(
              "Create a preset exam package. See features and exam settings for more information about setting options. This will provide a 'quick add' option for exams you administer often in the clinic."
            )}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              mt: "20px",
            }}
          >
            <Typography>{t("Click the Add button:")}</Typography>
            <Button sx={{ ...ModalCustomConfirm(), pointerEvents: "none" }}>
              {t("Add")}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={
                currentLang === "fr" ? clinicOverviewFrImg : clinicOverviewImg
              }
              width="100%"
              alt="clinic-overview"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
            }}
          >
            <img
              src={currentLang === "fr" ? clinicPackageFrImg : clinicPackageImg}
              width="100%"
              alt="clinic-package"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              mt: "20px",
            }}
          >
            <Typography>
              4. {t("Select your preferred report layout.")}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
            }}
          >
            <img
              src={
                currentLang === "fr"
                  ? reportLayoutSettingsFrImg
                  : reportLayoutSettingsImg
              }
              width="100%"
              alt="report-layout-settings"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              mt: "20px",
            }}
          >
            <Typography>
              5. {t("Select your eye report display order.")}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
            }}
          >
            <img
              src={
                currentLang === "fr" ? eyeDisplayOrderFrImg : eyeDisplayOrderImg
              }
              width="100%"
              alt="eye-display-order"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConfigureClinicSettings;
